import { useMemo, useState } from 'react';
import { Dropdown } from '@carbon/react';
import { SRPComboBox } from 'components/srp-combobox';
import classNames from 'classnames';
import { Toggle } from 'components/toggle';
import { ForecastOption } from './forecast-details';
import { hasId, withAcronymsUpperCased } from './utils';
import type {
  FilterValueOption,
  FilterValueSearch,
  Forecast,
  VolumeFilterBy,
  VolumeForecastType,
  VolumeSourcePickerData,
  VolumeSourceSelection,
} from './types';
import styles from './volume-source-picker.module.scss';

/** Stable empty list so the forecast memos do not see a new array every render. */
const NO_FORECASTS: Forecast[] = [];

/** Dropdown 1: how to narrow the list. */
const VOLUME_FILTER_ITEMS: { id: VolumeFilterBy; label: string }[] = [
  { id: 'type_curve_area', label: 'Type Curve Area' },
  { id: 'slot_id', label: 'Slot ID' },
];

/**
 * The full set a chosen forecast carries. `volumeSourceType` and `forecastSource` are
 * ROUTING fields — eval_calculator picks the volume pathway from them — so they travel with
 * the id: a type curve carries its href, a decline its forecast source + DCA scenario.
 *
 * @param {Forecast} item - The chosen forecast.
 * @param {VolumeForecastType} forecastType - The list it was chosen from.
 * @return {VolumeSourceSelection} The selection to report.
 */
const selectionFor = (
  item: Forecast,
  forecastType: VolumeForecastType
): VolumeSourceSelection => {
  const isTypeCurve = forecastType === 'type_curve';
  return {
    volumeSourceId: String(item.id),
    volumeSourceType: forecastType,
    volumeSourceHref: isTypeCurve && 'href' in item ? item.href : undefined,
    forecastSource:
      !isTypeCurve && 'forecastSource' in item
        ? item.forecastSource
        : undefined,
    dcaScenario:
      !isTypeCurve && 'dcaScenario' in item ? item.dcaScenario : undefined,
  };
};

/**
 * Dropdown 2's status. A branch with a character minimum (wells) sits idle until the user
 * types enough, and an idle search is not an empty result — reporting 'empty' there would
 * claim "no wells found" before anything had been searched for. Nor is it when a value is
 * already chosen and nothing has been typed (the inline editor opens on the row's own slot);
 * once the user types, too little text asks for more again.
 *
 * @param {string} searchText - The trimmed text typed into dropdown 2.
 * @param {number} minSearchChars - The branch's character minimum.
 * @param {VolumeSourcePickerData} data - The picker's data.
 * @return {string} The combobox status.
 */
const filterValueStatusFor = (
  searchText: string,
  minSearchChars: number,
  data: VolumeSourcePickerData
): FilterValueSearch['status'] => {
  if (searchText.length >= minSearchChars) return data.filterValues.status;
  return data.filterValue && searchText === '' ? 'ready' : 'empty';
};

/**
 * The forecast dropdown's status.
 *
 * @param {VolumeSourcePickerData} data - The picker's data.
 * @param {number} itemCount - How many forecasts the dropdown lists.
 * @return {string} The combobox status.
 */
const forecastStatusFor = (
  data: VolumeSourcePickerData,
  itemCount: number
): FilterValueSearch['status'] => {
  if (data.forecastsError) return 'error';
  if (data.forecastsLoading) return 'loading';
  return itemCount === 0 ? 'empty' : 'ready';
};

export interface VolumeSourcePickerProps {
  /** Prefix for the controls' DOM ids, so two pickers on one page cannot collide. */
  idPrefix: string;
  /** The narrowing state and the lists loaded for it, owned by the caller. */
  data: VolumeSourcePickerData;
  /** The chosen forecast's id, or null when nothing is chosen. */
  selectedId: string | null;
  /** Reports the chosen forecast as a full set, or null when the choice is cleared. */
  onSelect: (selection: VolumeSourceSelection | null) => void;
}

/**
 * The volume-source picker shared by Bulk Edit and the inline Edit Volume Source modal:
 * Filter by (Type Curve Area / Slot ID), then a server-searched area or well, then the
 * forecast — the area's type curves, or the well's PDP declines. Every upstream change
 * clears the chosen forecast, so a
 * half-changed set (e.g. a decline's DCA scenario stranded on a type curve) cannot reach
 * the caller.
 *
 * @param {VolumeSourcePickerProps} props - Narrowing data, selection, and callbacks.
 * @return {JSX.Element} The picker.
 */
export function VolumeSourcePicker({
  idPrefix,
  data,
  selectedId,
  onSelect,
}: VolumeSourcePickerProps) {
  const [filterValueSearch, setFilterValueSearch] = useState('');
  const [forecastSearch, setForecastSearch] = useState('');

  const isAreaBranch = data.filterBy === 'type_curve_area';
  const minSearchChars = data.minSearchChars ?? 0;

  // The way the user narrows decides what is listed: a well's slot offers its PDP declines,
  // a Type Curve Area its type curves. Copying a curve goes through its area, and declines
  // are per-well, so neither list needs the other.
  const forecastType: VolumeForecastType =
    data.filterBy === 'slot_id' ? 'pdp_decline' : 'type_curve';
  const pdpDeclines = data.forecasts?.pdpDeclines ?? NO_FORECASTS;
  const typeCurves = data.forecasts?.typeCurves ?? NO_FORECASTS;

  /** Clears everything downstream of the step the user just changed. */
  const resetForecast = () => {
    setForecastSearch('');
    onSelect(null);
  };

  /**
   * Dropdown 2 is searched SERVER-side, so the caller owns the text (it keys the fetch)
   * and the picker only reports keystrokes. The local copy is kept purely to decide
   * whether the user has typed enough for the branch to search at all.
   *
   * @param {string} text - The typed text.
   */
  const handleFilterValueSearch = (text: string) => {
    setFilterValueSearch(text);
    data.filterValues.onSearchChange(text);
  };

  // ── ⚠️ THE SELECTED OPTION'S IDENTITY FOLLOWS `data.filterValue` ALONE ────────────────
  //
  // Carbon's ComboBox compares `selectedItem` by identity, and when it changes it writes the
  // item's label back into the input and fires onChange. Re-resolving the chosen value
  // against each new page of search results minted a new object per result, so a search
  // landing mid-typing snapped the input back to the chosen well and the next keystroke was
  // appended to its label. So the option is the one the user picked, kept while the value is
  // unchanged; a value set from outside (the inline editor opens on the row's own slot) is
  // shown by its caller-given label, else its raw value, memoised on both.
  const [pickedFilterValue, setPickedFilterValue] =
    useState<FilterValueOption | null>(null);
  const valueOnlyOption = useMemo<FilterValueOption | null>(
    () =>
      data.filterValue
        ? {
            value: data.filterValue,
            label: data.filterValueLabel ?? data.filterValue,
          }
        : null,
    [data.filterValue, data.filterValueLabel]
  );
  const selectedFilterValue =
    pickedFilterValue?.value === data.filterValue
      ? pickedFilterValue
      : valueOnlyOption;

  const allForecasts = forecastType === 'type_curve' ? typeCurves : pdpDeclines;
  const inUseId = data.inUseId ?? null;

  const forecastItems = useMemo(() => {
    const text = forecastSearch.trim().toLowerCase();
    return text
      ? allForecasts.filter((item) =>
          (item.name ?? '').toLowerCase().includes(text)
        )
      : allForecasts;
  }, [allForecasts, forecastSearch]);

  // Resolve from the FULL list, not the filtered view, so typing a search that narrows the
  // chosen forecast out doesn't make the combobox look empty.
  const selectedForecast = useMemo(() => {
    if (selectedId === null) return null;
    return allForecasts.find((item) => hasId(item, selectedId)) ?? null;
  }, [allForecasts, selectedId]);

  /**
   * Turns the Archived toggle on or off. Off drops archived curves from the list, so a chosen
   * one is cleared rather than saved unseen — unless it is the row's current curve, which the
   * list keeps.
   *
   * @param {boolean} show - Whether to list archived curves.
   */
  const handleShowArchived = (show: boolean) => {
    if (
      !show &&
      selectedForecast &&
      'archived' in selectedForecast &&
      selectedForecast.archived &&
      !selectedForecast.isCurrent
    ) {
      resetForecast();
    }
    data.onShowArchivedChange(show);
  };

  const trimmedFilterValueSearch = filterValueSearch.trim();
  const filterValueEmptyText =
    trimmedFilterValueSearch.length < minSearchChars
      ? `Type at least ${minSearchChars} characters to search`
      : 'No matches';

  return (
    <>
      <div className={styles.field}>
        <Dropdown<(typeof VOLUME_FILTER_ITEMS)[number]>
          id={`${idPrefix}-filter-by`}
          titleText="Filter by"
          label="Select how to find a volume source"
          items={VOLUME_FILTER_ITEMS}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={
            VOLUME_FILTER_ITEMS.find((item) => item.id === data.filterBy) ??
            undefined
          }
          onChange={({ selectedItem }) => {
            data.onFilterByChange(selectedItem?.id ?? null);
            data.onFilterValueChange(null);
            // Clear BOTH copies of the search text: the local one gating the character
            // minimum, and the caller's, which keys the fetch — a leftover well query
            // would otherwise be sent to the areas endpoint.
            handleFilterValueSearch('');
            resetForecast();
          }}
        />
      </div>

      {data.filterBy && (
        <div className={styles.field}>
          <SRPComboBox<FilterValueOption>
            id={`${idPrefix}-filter-value`}
            titleText={isAreaBranch ? 'Type Curve Area' : 'Well'}
            placeholder={
              isAreaBranch
                ? 'Search type curve areas'
                : 'Search wells by UWI or name'
            }
            items={data.filterValues.items}
            itemToString={(item) => item?.label ?? ''}
            selectedItem={selectedFilterValue}
            onSelect={(item) => {
              setPickedFilterValue(item);
              data.onFilterValueChange(item?.value ?? null);
              resetForecast();
            }}
            onSearchChange={handleFilterValueSearch}
            status={filterValueStatusFor(
              trimmedFilterValueSearch,
              minSearchChars,
              data
            )}
            errorMessage={data.filterValues.errorMessage}
            errorTitle={
              isAreaBranch
                ? 'Could not load type curve areas'
                : 'Could not search wells'
            }
            loadingText={
              isAreaBranch ? 'Loading type curve areas...' : 'Searching...'
            }
            emptyText={filterValueEmptyText}
          />
        </div>
      )}

      {data.filterBy && data.filterValue && (
        <>
          <div className={classNames(styles.field, styles.forecastField)}>
            {forecastType === 'type_curve' && (
              <Toggle
                isActive={data.showArchived}
                handleToggle={handleShowArchived}
                label="Archived"
                className={styles.archivedToggle}
              />
            )}
            <SRPComboBox<Forecast>
              id={`${idPrefix}-source`}
              titleText="Volume Source"
              placeholder="Search forecasts"
              items={forecastItems}
              itemToString={(item) => withAcronymsUpperCased(item?.name ?? '')}
              renderItem={(item) => (
                <ForecastOption
                  forecast={item}
                  forecastType={forecastType}
                  inUse={inUseId !== null && hasId(item, inUseId)}
                />
              )}
              selectedItem={selectedForecast}
              onSelect={(item) => {
                onSelect(item ? selectionFor(item, forecastType) : null);
              }}
              onSearchChange={setForecastSearch}
              status={forecastStatusFor(data, forecastItems.length)}
              errorMessage={data.forecastsError}
              errorTitle="Could not load forecasts"
              loadingText="Loading forecasts..."
              emptyText={
                forecastType === 'pdp_decline'
                  ? 'No PDP declines for this well'
                  : 'No type curves in this area'
              }
              className={styles.forecastCombo}
            />
          </div>
        </>
      )}
    </>
  );
}
